import { COMPACT_HEADER_STYLE } from '../../lib/gameChrome'
import React, { useEffect, useState } from 'react'
import { ActivityIndicator, AppState, FlatList, ScrollView, Text, TouchableOpacity, View } from 'react-native'
import { useRouter, useFocusEffect, type Href } from 'expo-router'
import { openNewsLink } from '../../lib/openNewsLink'
import { SafeAreaView } from 'react-native-safe-area-context'
import { randomUUID } from 'expo-crypto'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { SupportConversation, SupportTheme, timeAgo, type SupportRequest } from '@warcarp/ui'
import type { SupportInboxResponse, SupportTicket } from '@warcarp/api-types'
import { newsAppRoute } from '@warcarp/api-types'
import { useTheme } from '../../hooks/useTheme'
import { MessageBubble } from '../game/MessageThread'
import { apiFetch, HttpError } from '../../lib/api'
import { goBackOr } from '../../lib/goBack'
import { Button } from '../ui/Button'
import { AppModal } from '../ui/AppModal'
import { ReportedMessageBubble } from './ReportedMessageBubble'

const request: SupportRequest = async <T,>(path: string, method = 'GET', body?: unknown): Promise<T> => {
  const res = await apiFetch(path, { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) })
  if (!res.ok) throw new HttpError(res.status, 'Could not load or update your ticket. Please try again.')
  return res.json() as Promise<T>
}

export function SupportScreen({ ticketId }: { ticketId?: string }) {
  const router = useRouter()
  const { theme } = useTheme()
  const client = useQueryClient()
  const [focused, setFocused] = useState(false)
  useFocusEffect(React.useCallback(() => { setFocused(true); return () => setFocused(false) }, []))
  const [active, setActive] = useState(AppState.currentState === 'active')
  const [selected, setSelected] = useState(ticketId)
  const [refreshing, setRefreshing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const selectedTicket = useQuery({ queryKey: ['support', false, selected],
    queryFn: () => request<SupportTicket>(`/api/v1/support-tickets/${encodeURIComponent(selected!)}`), enabled: !!selected,
  })
  const isNews = selectedTicket.data?.type === 'NEWS'
  useEffect(() => { setSelected(ticketId) }, [ticketId])
  useEffect(() => {
    const listener = AppState.addEventListener('change', (state) => setActive(state === 'active'))
    return () => listener.remove()
  }, [])
  const list = useInfiniteQuery({ queryKey: ['support', 'player-inbox'], initialPageParam: '',
    queryFn: ({ pageParam }) => request<SupportInboxResponse>(`/api/v1/support-tickets/inbox?limit=30${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`),
    getNextPageParam: (page) => page.nextCursor ?? undefined, refetchInterval: 10000,
  })
  const read = useMutation({ mutationFn: ({ id, readThrough }: { id: string; readThrough: string }) => request(`/api/v1/support-tickets/${encodeURIComponent(id)}/read`, 'POST', { readThrough }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['support'] }),
  })
  const remove = useMutation({
    mutationFn: (id: string) => request(`/api/v1/support-tickets/${encodeURIComponent(id)}`, 'DELETE'),
    onSuccess: async () => {
      setConfirmDelete(false)
      setSelected(undefined)
      read.reset()
      if (ticketId) router.replace('/(app)/settings/inbox')
      await client.invalidateQueries({ queryKey: ['support'] })
    },
  })
  function back() {
    if (remove.isPending) return
    setConfirmDelete(false)
    if (selected && !ticketId) {
      setSelected(undefined)
      read.reset()
    } else {
      goBackOr(router, ticketId ? '/(app)/settings/inbox' : '/(app)/settings')
    }
  }
  async function refreshInbox() {
    setRefreshing(true)
    try { await list.refetch() } finally { setRefreshing(false) }
  }
  return <SafeAreaView className="flex-1 bg-canvas">
    <View className="flex-row items-center gap-3 px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
      <TouchableOpacity accessibilityRole="button" accessibilityLabel="Back" className="p-1" onPress={back}><Text className="font-typed text-blood text-base">← Back</Text></TouchableOpacity>
      <Text className="font-stencil text-ink text-xl flex-1">Inbox</Text>
      {!selected && <Button title="New" size="sm" accessibilityLabel="New message" hitSlop={6} style={{ flexShrink: 0 }} onPress={() => router.push('/(app)/settings/contact')} />}
      {!!selected && <Button title={isNews ? 'Remove' : 'Delete'} size="sm" accessibilityLabel={isNews ? 'Remove news post' : 'Delete conversation'} hitSlop={6} style={{ flexShrink: 0 }} disabled={remove.isPending} onPress={() => { remove.reset(); setConfirmDelete(true) }} />}
    </View>
    {confirmDelete && selected && <AppModal visible transparent animationType="fade" onRequestClose={() => { if (!remove.isPending) setConfirmDelete(false) }}>
      <View className="flex-1 items-center justify-center bg-black/60 p-6">
        <View className="w-full p-5 gap-4 rounded-2xl border-2 border-olive bg-paper" style={{ maxWidth: 480, maxHeight: '90%' }} accessibilityViewIsModal>
          <Text className="font-stencil text-ink text-lg" accessibilityRole="header">{isNews ? 'Remove news post?' : 'Delete conversation?'}</Text>
          <ScrollView>
            <Text className="font-typed text-ink">{isNews ? 'Remove this news post from your messages? This removes it from your inbox only.' : 'WarCarp will no longer be able to contact you through this conversation. It will be removed from your inbox, and you will not receive further replies or notifications for it.'}</Text>
            {!isNews && <Text className="font-typed text-ink-soft text-sm mt-3">WarCarp retains the report and message history for support and moderation.</Text>}
            {remove.error && <Text accessibilityRole="alert" className="font-typed text-blood mt-3">Could not delete. Please try again.</Text>}
          </ScrollView>
          <Button title={isNews ? 'Remove news post' : 'Delete conversation'} variant="danger" loading={remove.isPending} onPress={() => remove.mutate(selected)} />
          <Button title="Cancel" variant="secondary" disabled={remove.isPending} onPress={() => setConfirmDelete(false)} />
        </View>
      </View>
    </AppModal>}
    {selected ? <SupportTheme dark={theme === 'dark'} chatSurface>
      {read.error && <Text accessibilityRole="alert" className="text-blood p-3">Could not mark as read. Reopen this conversation to retry.</Text>}
      <SupportConversation key={selected} ticketId={selected} admin={false} compact request={request} newMessageId={randomUUID}
        onOpenLink={href => openNewsLink(href, path => router.push(path as Href))}
        renderUnreadDivider={dismissed => <NewMessageDivider dismissed={dismissed} />}
        onBack={back}
        renderHeader={(subject) => <View className="px-4 py-2 border-b border-olive"><Text className="font-stencil text-ink text-base">{subject}</Text></View>}
        renderEvidence={(message) => <View className="border-b border-olive" style={{ gap: 12, paddingBottom: 12, marginBottom: 8 }}>
          <Text className="font-stencil text-ink-soft text-xs">Flagged as inappropriate</Text>
          <ReportedMessageBubble message={message} topSpacing={0} />
        </View>}
        onReadThrough={active && focused ? (readThrough) => read.mutate({ id: selected, readThrough }) : undefined}
        renderMessage={(message) => <MessageBubble item={{ id: message.id, authorId: message.authorId ?? 'deleted', authorName: message.fromAdmin ? 'Warcarp' : message.authorName, content: message.body, createdAt: message.createdAt }} isOwn={!message.fromAdmin} showMeta suppressTextSelection={false} readOnly
          onOpenLink={href => {
            const path = newsAppRoute(href)
            if (!path) return false
            router.push(path as Href)
            return true
          }} />} />
    </SupportTheme> : <>
      {list.isPending && <ActivityIndicator />}
      {list.error && <TouchableOpacity accessibilityRole="button" onPress={() => void list.refetch()}><Text accessibilityRole="alert" className="text-blood p-4">Could not load messages. Tap to retry.</Text></TouchableOpacity>}
      <FlatList data={[...new Map((list.data?.pages.flatMap((page) => page.items) ?? []).map((item) => [item.id, item])).values()]}
        contentContainerStyle={{ flexGrow: 1 }}
        keyExtractor={(item) => item.id} refreshing={refreshing} onRefresh={() => void refreshInbox()}
        onEndReached={() => { if (list.hasNextPage && !list.isFetchingNextPage) void list.fetchNextPage() }}
        ListEmptyComponent={!list.isPending && !list.error ? <View className="flex-1 items-center justify-center px-6 py-8">
          <View className="w-full gap-4" style={{ maxWidth: 420 }}>
            <Text className="font-stencil text-ink text-xl text-center">No messages yet</Text>
            <Text className="font-typed text-ink-soft text-base text-center">Share an idea, report a bug, or ask us a question.</Text>
            <Button title="New message" size="lg" testID="empty-support-new-message" onPress={() => router.push('/(app)/settings/contact')} />
          </View>
        </View> : null}
        ListFooterComponent={list.isFetchingNextPage ? <ActivityIndicator /> : null}
        renderItem={({ item }) => <TouchableOpacity testID={`support-inbox-item-${item.id}`} accessibilityRole="button" accessibilityLabel={`${item.unread ? 'Unread: ' : ''}${item.type === 'NEWS' ? 'News post: ' : ''}${item.subject}`} onPress={() => setSelected(item.id)} className="bg-paper border-b border-olive p-4 gap-1">
          <View className="flex-row items-center gap-2">
            {item.unread && <View className="bg-blood rounded-full" style={{ width: 8, height: 8 }} />}
            <Text className="font-typed text-ink flex-1" style={{ fontWeight: item.unread ? '700' : '400' }}>{item.type === 'NEWS' ? 'Warcarp News' : item.sender}</Text>
            <Text className="font-typed text-ink-soft text-xs">{timeAgo(item.lastMessageAt)}</Text>
          </View>
          <Text className="font-stencil text-ink">{item.subject}</Text>
          <Text numberOfLines={2} className="font-typed text-ink-soft">{item.preview}</Text>
        </TouchableOpacity>} />
    </>}
  </SafeAreaView>
}
import { NewMessageDivider } from '../game/NewMessageDivider'
